import { FC, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useHelpContext, HelpContent } from "../../../context/help.context";

const HelpDialog: FC = () => {
  const { currentScreenId, currentFieldId, hideHelp, getHelpContent } =
    useHelpContext();

  const helpContent: HelpContent | null = currentScreenId
    ? getHelpContent(currentScreenId, currentFieldId || undefined)
    : null;

  const handleClose = () => {
    hideHelp();
  };

  if (!currentScreenId) {
    return null;
  }

  // If no help content found, show a default message
  if (!helpContent) {
    return (
      <Dialog
        open={!!currentScreenId}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        data-testid="help-dialog"
      >
        <DialogTitle data-testid="help-dialog-title">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Help</Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              data-testid="help-dialog-close-button"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-testid="help-dialog-content">
          <Typography variant="body1" paragraph>
            Help content for this screen is not available yet.
          </Typography>
        </DialogContent>
        <DialogActions data-testid="help-dialog-actions">
          <Button
            onClick={handleClose}
            variant="contained"
            data-testid="help-dialog-close-action-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const fieldHelp = currentFieldId && helpContent.fields?.[currentFieldId];

  return (
    <Dialog
      open={!!currentScreenId}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
      data-testid="help-dialog"
    >
      <DialogTitle data-testid="help-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{helpContent.title}</Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            data-testid="help-dialog-close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent data-testid="help-dialog-content">
        <Box sx={{ py: 2 }} data-testid="help-dialog-content-container">
          {fieldHelp ? (
            <>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {fieldHelp.label}
              </Typography>
              <Typography variant="body2" paragraph color="text.secondary">
                {fieldHelp.description}
              </Typography>
              {fieldHelp.indianContext && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Indian Educational Context
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fieldHelp.indianContext}
                  </Typography>
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph>
                {helpContent.description}
              </Typography>
              {helpContent.indianContext && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Indian Educational Context
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {helpContent.indianContext}
                  </Typography>
                </>
              )}
              {helpContent.fields &&
                Object.keys(helpContent.fields).length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontWeight="bold"
                    >
                      Field Help
                    </Typography>
                    {Object.entries(helpContent.fields).map(
                      ([fieldId, fieldInfo]) => (
                        <Box key={fieldId} sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {fieldInfo.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {fieldInfo.description}
                          </Typography>
                          {fieldInfo.indianContext && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              <strong>Note:</strong> {fieldInfo.indianContext}
                            </Typography>
                          )}
                        </Box>
                      ),
                    )}
                  </>
                )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions data-testid="help-dialog-actions">
        <Button
          onClick={handleClose}
          variant="contained"
          data-testid="help-dialog-close-action-button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog;
